/**
 * The Earthworks daily-log form on the phone (EW-4, fieldiq-api#1284).
 * SNAKE_CASE in (the /field read) and out (the PUT body).
 *
 * EVERY SET IS SENT AND EVERY SET REPLACES: crew, equipment, loads, dump
 * tickets and photos go in full on each save, so a row removed here is a row
 * removed there.
 *
 * - Crew and unit rows: hours blank = not on the log. Hours and standby 0–24.
 * - The hour-meter end is offered on an OWNED unit only (the API refuses it
 *   on a rent-in: meter_owned_only). A rent-in is offered only on a day it is
 *   on rent, unless the log already holds it.
 * - A load's quantity is in the tenant's volume unit from `meta.units.volume`
 *   (m³ metric, yd³ imperial); a dump ticket's weight defaults to
 *   `meta.units.mass`. Never a literal.
 * - Files (dump-ticket photo, before / after photos) upload when picked, online
 *   only, and say so loudly when they cannot.
 * - WARNINGS NEVER DISABLE SAVE. Each one has a "Carry on" tap; the kinds
 *   tapped go with the save as `acknowledge_warnings`.
 * - EW-9 materials: items from inventory and how much was used (2 decimals at
 *   most — the use bills on a ticket line). The picker shows on hand, never a
 *   price. Low or below-zero stock is the API's warning after the save; it
 *   never refuses. A log read from before inventory (no `materials` key) does
 *   not send the set, so an old read cannot return stock by omission.
 */
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Camera, ClipboardCheck, Plus, Trash2 } from 'lucide-react';
import { uploadEwJobFile } from '../../../hooks/useEwField';
import { useEwFieldMaterials } from '../../../hooks/useEwInventory';
import { blankToNull, catalogLabel, ewDay, toInput } from '../../../lib/earthworks';
import type {
  EwCatalog,
  EwCatalogEntry,
  EwDailyLogWrite,
  EwFieldJobDetail,
  EwFieldLog,
  EwFieldMeta,
  EwPreUseCheck,
} from '../../../types/earthworks';
import { EW_INPUT, EW_PRIMARY, EW_SECONDARY, EwField, EwFormError } from '../EwBits';
import { EwWarningCards, type EwWarningView } from '../EwWarnings';

// ─── Rows ────────────────────────────────────────────────────────────────────

interface CrewRow {
  userId: string;
  name: string;
  hours: string;
  standby: string;
  notes: string;
}

export interface EwUnitOption {
  key: string;
  kind: 'owned' | 'rent_in';
  equipmentId: string | null;
  rentInId: string | null;
  name: string;
  detail: string | null;
  onRentOn?: string | null;
  offRentOn?: string | null;
}

interface UnitRow extends EwUnitOption {
  hours: string;
  standby: string;
  meter: string;
  checkId: string;
  notes: string;
}

interface LoadRow {
  rid: string;
  direction: string;
  material: string;
  count: string;
  quantity: string;
  quantityUnit: string;
  hauler: string;
  notes: string;
}

interface DumpRow {
  rid: string;
  ticketNumber: string;
  facility: string;
  material: string;
  weight: string;
  weightUnit: string;
  fee: string;
  uploadId: string | null;
  photoName: string | null;
  photoUrl: string | null;
  notes: string;
}

interface MaterialRow {
  rid: string;
  materialId: string;
  locationId: string;
  quantity: string;
  /** Read back from the log with no location: a blank keeps what the log used, not the item's home. */
  loggedBlank: boolean;
}

interface PhotoRow {
  rid: string;
  stage: string;
  uploadId: string;
  name: string | null;
  url: string | null;
  caption: string;
}

let ridSeq = 0;
const rid = () => `r${++ridSeq}`;

const unitKey = (kind: string, equipmentId: string | null, rentInId: string | null) =>
  kind === 'rent_in' ? `rent_in:${rentInId}` : `owned:${equipmentId}`;

const onRent = (u: EwUnitOption, day: string) =>
  u.kind !== 'rent_in' || ((!u.onRentOn || u.onRentOn <= day) && (!u.offRentOn || u.offRentOn >= day));

function hoursError(label: string, v: string): string | null {
  if (!v.trim()) return null;
  const n = Number(v);
  if (!Number.isFinite(n) || n < 0 || n > 24) return `${label}: hours must be between 0 and 24.`;
  return null;
}

function amountError(label: string, v: string): string | null {
  if (!v.trim()) return null;
  const n = Number(v);
  if (!Number.isFinite(n) || n < 0) return `${label} must be a number, 0 or more.`;
  return null;
}

/** The job's units as plain options: every owned unit and every rent-in. */
export function ewUnitOptions(job: EwFieldJobDetail): EwUnitOption[] {
  return [
    ...(job.equipment ?? []).map((e) => ({
      key: `owned:${e.equipment_id}`,
      kind: 'owned' as const,
      equipmentId: e.equipment_id,
      rentInId: null,
      name: e.equipment_name ?? e.unit_number ?? '—',
      detail: e.unit_number,
    })),
    ...(job.rent_ins ?? []).map((r) => ({
      key: `rent_in:${r.id}`,
      kind: 'rent_in' as const,
      equipmentId: r.equipment_id,
      rentInId: r.id,
      name: r.description || r.vendor_name,
      detail: [r.vendor_name, r.unit_number].filter(Boolean).join(' · ') || null,
      onRentOn: r.on_rent_on,
      offRentOn: r.off_rent_on,
    })),
  ];
}

/** The write body from the form. Exported for the test. */
export function ewLogWriteBody(
  header: { workKinds: string[]; weather: string; narrative: string; safety: string },
  crew: CrewRow[],
  units: UnitRow[],
  loads: LoadRow[],
  dumps: DumpRow[],
  photos: PhotoRow[],
  carried: string[],
  materials: MaterialRow[] | null = null,
): EwDailyLogWrite {
  return {
    work_kinds: header.workKinds,
    weather: blankToNull(header.weather),
    narrative: blankToNull(header.narrative),
    safety_notes: blankToNull(header.safety),
    crew: crew
      .filter((c) => c.hours.trim() !== '')
      .map((c) => ({ user_id: c.userId, hours: c.hours.trim(), standby_hours: blankToNull(c.standby), notes: blankToNull(c.notes) })),
    equipment: units
      .filter((u) => u.hours.trim() !== '')
      .map((u) => ({
        ...(u.kind === 'rent_in' ? { rent_in_id: u.rentInId as string } : { equipment_id: u.equipmentId as string }),
        hours: u.hours.trim(),
        standby_hours: blankToNull(u.standby),
        // Owned units only — a rent-in never sends a meter.
        ...(u.kind === 'owned' ? { hour_meter_end: blankToNull(u.meter) } : {}),
        equipment_check_id: blankToNull(u.checkId),
        notes: blankToNull(u.notes),
      })),
    loads: loads.map((l) => {
      const quantity = blankToNull(l.quantity);
      return {
        direction: l.direction,
        material: l.material,
        load_count: Number(l.count),
        quantity,
        quantity_unit: quantity === null ? null : l.quantityUnit || null,
        hauler_name: blankToNull(l.hauler),
        notes: blankToNull(l.notes),
      };
    }),
    dump_tickets: dumps.map((d) => {
      const weight = blankToNull(d.weight);
      return {
        ticket_number: d.ticketNumber.trim(),
        facility_name: d.facility.trim(),
        material: d.material || null,
        net_weight: weight,
        weight_unit: weight === null ? null : d.weightUnit || null,
        tipping_fee: blankToNull(d.fee),
        upload_id: d.uploadId,
        notes: blankToNull(d.notes),
      };
    }),
    photos: photos.map((p) => ({ stage: p.stage, upload_id: p.uploadId, caption: blankToNull(p.caption) })),
    // null = this read did not carry the set: leave the log's materials alone.
    ...(materials === null
      ? {}
      : { materials: materials.map((m) => ({ material_id: m.materialId, location_id: m.locationId || null, quantity: m.quantity.trim() })) }),
    acknowledge_warnings: carried,
  };
}

const SECTION_TITLE = 'mb-2 text-[0.68rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted';
const ROW_CARD = 'space-y-2 rounded-fi-control border border-fieldiq-border bg-fieldiq-dark/40 p-3';

function Options({ list, blank }: { list: readonly EwCatalogEntry[] | undefined; blank?: string }) {
  return (
    <>
      {blank !== undefined && <option value="">{blank}</option>}
      {(list ?? []).map((e) => (
        <option key={e.value} value={e.value}>
          {e.label}
        </option>
      ))}
    </>
  );
}

function RowRemove({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" aria-label={label} onClick={onClick} className="inline-flex items-center gap-1 text-xs font-bold text-fieldiq-muted hover:text-white">
      <Trash2 className="size-4" aria-hidden="true" />
      Remove
    </button>
  );
}

/** A file input that uploads on pick; the parent learns the upload id only when it landed. */
function FilePick({
  label,
  testId,
  disabled,
  onPick,
}: {
  label: string;
  testId: string;
  disabled?: boolean;
  onPick: (file: File) => void;
}) {
  return (
    <label className={`${EW_SECONDARY} cursor-pointer ${disabled ? 'pointer-events-none opacity-60' : ''}`}>
      <Camera className="size-4" aria-hidden="true" />
      {label}
      <input
        type="file"
        accept="image/*"
        capture="environment"
        className="sr-only"
        aria-label={label}
        data-testid={testId}
        disabled={disabled}
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (f) onPick(f);
        }}
      />
    </label>
  );
}

export function EwLogForm({
  jobId,
  workDate,
  job,
  log,
  catalog,
  meta,
  checks,
  warnings,
  userId,
  pending,
  error,
  submitLabel,
  readOnly = false,
  fileCheckState,
  onSubmit,
}: {
  jobId: string;
  workDate: string;
  job: EwFieldJobDetail;
  log: EwFieldLog | null;
  catalog: EwCatalog | null;
  meta: EwFieldMeta | null;
  checks: EwPreUseCheck[];
  warnings: EwWarningView[];
  userId: string | null;
  pending: boolean;
  error: string | null;
  submitLabel: string;
  readOnly?: boolean;
  fileCheckState?: (unit: EwUnitOption) => { to: string; state: unknown } | null;
  onSubmit: (body: EwDailyLogWrite) => void;
}) {
  const volume = meta?.units?.volume ?? null;
  const mass = meta?.units?.mass ?? null;
  const currency = meta?.currency ?? null;
  const timeZone = meta?.time_zone ?? null;

  const [header, setHeader] = useState({
    workKinds: (log?.work_kinds ?? []).map((k) => k.value),
    weather: log?.weather ?? '',
    narrative: log?.narrative ?? '',
    safety: log?.safety_notes ?? '',
  });

  const initialCrew = useMemo<CrewRow[]>(() => {
    const rows = new Map<string, CrewRow>();
    for (const c of job.crew ?? []) rows.set(c.user_id, { userId: c.user_id, name: c.user_name ?? '—', hours: '', standby: '', notes: '' });
    for (const c of log?.crew ?? [])
      rows.set(c.user_id, {
        userId: c.user_id,
        name: c.user_name ?? rows.get(c.user_id)?.name ?? '—',
        hours: toInput(c.hours),
        standby: c.standby_hours ? toInput(c.standby_hours) : '',
        notes: c.notes ?? '',
      });
    return Array.from(rows.values());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initialUnits = useMemo<UnitRow[]>(() => {
    const options = ewUnitOptions(job);
    const rows = new Map<string, UnitRow>();
    for (const u of options) if (onRent(u, workDate)) rows.set(u.key, { ...u, hours: '', standby: '', meter: '', checkId: '', notes: '' });
    for (const e of log?.equipment ?? []) {
      const key = unitKey(e.kind, e.equipment_id, e.rent_in_id);
      const opt = options.find((u) => u.key === key);
      rows.set(key, {
        key,
        kind: e.kind === 'rent_in' ? 'rent_in' : 'owned',
        equipmentId: opt?.equipmentId ?? e.equipment_id,
        rentInId: e.rent_in_id,
        name: e.unit_name ?? opt?.name ?? '—',
        detail: opt?.detail ?? null,
        hours: toInput(e.hours),
        standby: e.standby_hours ? toInput(e.standby_hours) : '',
        meter: toInput(e.hour_meter_end),
        checkId: e.equipment_check_id ?? '',
        notes: e.notes ?? '',
      });
    }
    return Array.from(rows.values());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [crew, setCrew] = useState<CrewRow[]>(initialCrew);
  const [units, setUnits] = useState<UnitRow[]>(initialUnits);
  const [loads, setLoads] = useState<LoadRow[]>(() =>
    (log?.loads ?? []).map((l) => ({
      rid: rid(),
      direction: l.direction,
      material: l.material,
      count: toInput(l.load_count),
      quantity: toInput(l.quantity),
      quantityUnit: l.quantity_unit ?? volume ?? '',
      hauler: l.hauler_name ?? '',
      notes: l.notes ?? '',
    })),
  );
  const [dumps, setDumps] = useState<DumpRow[]>(() =>
    (log?.dump_tickets ?? []).map((d) => ({
      rid: rid(),
      ticketNumber: d.ticket_number,
      facility: d.facility_name,
      material: d.material ?? '',
      weight: toInput(d.net_weight),
      weightUnit: d.weight_unit ?? mass ?? '',
      fee: toInput(d.tipping_fee),
      uploadId: d.photo?.upload_id ?? null,
      photoName: d.photo?.file_name ?? null,
      photoUrl: d.photo?.url ?? null,
      notes: d.notes ?? '',
    })),
  );
  const [photos, setPhotos] = useState<PhotoRow[]>(() =>
    (log?.photos ?? [])
      .filter((p) => p.file?.upload_id)
      .map((p) => ({ rid: rid(), stage: p.stage, uploadId: p.file!.upload_id, name: p.file?.file_name ?? null, url: p.file?.url ?? null, caption: p.caption ?? '' })),
  );
  const materialsKnown = !log || Array.isArray(log.materials);
  const [materials, setMaterials] = useState<MaterialRow[]>(() =>
    (log?.materials ?? []).map((m) => ({ rid: rid(), materialId: m.material_id, locationId: m.location_id ?? '', quantity: toInput(m.quantity), loggedBlank: !m.location_id })),
  );
  const picker = useEwFieldMaterials(materialsKnown);
  const stockItems = picker.data?.data ?? [];
  const stockLocations = picker.data?.locations ?? [];
  const itemOf = (materialId: string) => stockItems.find((i) => i.id === materialId) ?? null;
  const logItemOf = (materialId: string) => (log?.materials ?? []).find((m) => m.material_id === materialId) ?? null;
  const setMaterial = (id: string, patch: Partial<MaterialRow>) => setMaterials((rows) => rows.map((r) => (r.rid === id ? { ...r, ...patch } : r)));
  const [carried, setCarried] = useState<Set<string>>(() => new Set());
  const [photoStage, setPhotoStage] = useState('before');
  const [uploading, setUploading] = useState(0);
  const [localError, setLocalError] = useState<string | null>(null);

  const setCrewRow = (i: number, patch: Partial<CrewRow>) => setCrew((rows) => rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const setUnitRow = (i: number, patch: Partial<UnitRow>) => setUnits((rows) => rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const setLoad = (id: string, patch: Partial<LoadRow>) => setLoads((rows) => rows.map((r) => (r.rid === id ? { ...r, ...patch } : r)));
  const setDump = (id: string, patch: Partial<DumpRow>) => setDumps((rows) => rows.map((r) => (r.rid === id ? { ...r, ...patch } : r)));
  const toggleKind = (v: string) =>
    setHeader((h) => ({ ...h, workKinds: h.workKinds.includes(v) ? h.workKinds.filter((k) => k !== v) : [...h.workKinds, v] }));
  const toggleCarry = (kind: string) =>
    setCarried((s) => {
      const next = new Set(s);
      if (next.has(kind)) next.delete(kind);
      else next.add(kind);
      return next;
    });

  const upload = async (file: File, done: (uploadId: string) => void) => {
    setLocalError(null);
    setUploading((n) => n + 1);
    try {
      done(await uploadEwJobFile(jobId, file));
    } catch (err) {
      setLocalError(err instanceof Error && err.message ? `The photo was not attached. ${err.message}` : 'The photo was not attached. Try again.');
    } finally {
      setUploading((n) => n - 1);
    }
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    if (uploading > 0) return setLocalError('A photo is still uploading. Wait for it to finish, then save.');
    for (const c of crew) {
      const err = hoursError(c.name, c.hours) ?? (c.hours.trim() ? hoursError(`${c.name} standby`, c.standby) : null);
      if (err) return setLocalError(err);
    }
    for (const u of units) {
      const err = hoursError(u.name, u.hours) ?? (u.hours.trim() ? hoursError(`${u.name} standby`, u.standby) : null);
      if (err) return setLocalError(err);
      if (!u.hours.trim() && (u.meter.trim() || u.checkId)) return setLocalError(`${u.name}: enter its hours, or clear the meter and check.`);
      if (u.kind === 'owned') {
        const m = amountError(`${u.name}: the hour meter`, u.meter);
        if (m) return setLocalError(m);
      }
    }
    for (let i = 0; i < loads.length; i++) {
      const l = loads[i];
      const n = Number(l.count);
      if (!l.material) return setLocalError(`Load ${i + 1}: choose the material.`);
      if (!l.count.trim() || !Number.isInteger(n) || n < 0 || n > 1000) return setLocalError(`Load ${i + 1}: the load count must be a whole number from 0 to 1000.`);
      const q = amountError(`Load ${i + 1}: the quantity`, l.quantity);
      if (q) return setLocalError(q);
      if (l.quantity.trim() && !l.quantityUnit) return setLocalError(`Load ${i + 1}: choose the quantity's unit.`);
    }
    for (let i = 0; i < dumps.length; i++) {
      const d = dumps[i];
      if (!d.ticketNumber.trim()) return setLocalError(`Dump ticket ${i + 1}: enter the ticket number.`);
      if (!d.facility.trim()) return setLocalError(`Dump ticket ${i + 1}: enter the facility.`);
      const w = amountError(`Dump ticket ${i + 1}: the weight`, d.weight) ?? amountError(`Dump ticket ${i + 1}: the tipping fee`, d.fee);
      if (w) return setLocalError(w);
      if (d.weight.trim() && !d.weightUnit) return setLocalError(`Dump ticket ${i + 1}: choose the weight's unit.`);
    }
    for (let i = 0; i < materials.length; i++) {
      const m = materials[i];
      if (!m.materialId) return setLocalError(`Material ${i + 1}: choose the item.`);
      if (!/^\d+(\.\d{1,2})?$/.test(m.quantity.trim()) || Number(m.quantity) <= 0) {
        return setLocalError(`Material ${i + 1}: enter the quantity used, above 0, with at most 2 decimals.`);
      }
    }
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      return setLocalError('You are offline. The daily log was NOT saved — save again when you have signal.');
    }
    onSubmit(ewLogWriteBody(header, crew, units, loads, dumps, photos, Array.from(carried), materialsKnown ? materials : null));
  };

  const checksFor = (u: UnitRow) => (u.equipmentId ? checks.filter((c) => c.equipmentId === u.equipmentId) : []);
  const quantityLabel = (l: LoadRow) => `Quantity${l.quantityUnit ? ` (${catalogLabel(catalog?.quantityUnits, l.quantityUnit)})` : ''}`;

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="ew-log-form">
      <EwWarningCards
        warnings={warnings}
        timeZone={timeZone}
        userId={userId}
        carried={carried}
        onToggle={readOnly ? undefined : toggleCarry}
        testId="ew-log-warnings"
      />
      <EwFormError message={localError ?? error} />
      <fieldset disabled={readOnly} className="space-y-5">
        <section data-testid="ew-log-work-kinds">
          <h3 className={SECTION_TITLE}>Work done today</h3>
          <div className="flex flex-wrap gap-2">
            {(catalog?.workKinds ?? []).map((k) => {
              const on = header.workKinds.includes(k.value);
              return (
                <button
                  key={k.value}
                  type="button"
                  aria-pressed={on}
                  onClick={() => toggleKind(k.value)}
                  className={`min-h-[44px] rounded-fi-pill border px-3 text-sm font-bold ${on ? 'border-fi-amber bg-fi-amber/15 text-white' : 'border-fieldiq-border text-fieldiq-text'}`}
                >
                  {k.label}
                </button>
              );
            })}
          </div>
        </section>

        <div className="grid gap-3">
          <EwField label="Weather">
            <input className={EW_INPUT} aria-label="Weather" value={header.weather} onChange={(e) => setHeader({ ...header, weather: e.target.value })} />
          </EwField>
          <EwField label="Notes on the work">
            <textarea className={`${EW_INPUT} h-24 py-2`} aria-label="Notes on the work" value={header.narrative} onChange={(e) => setHeader({ ...header, narrative: e.target.value })} />
          </EwField>
          <EwField label="Safety notes">
            <textarea className={`${EW_INPUT} h-20 py-2`} aria-label="Safety notes" value={header.safety} onChange={(e) => setHeader({ ...header, safety: e.target.value })} />
          </EwField>
        </div>

        <section data-testid="ew-log-crew">
          <h3 className={SECTION_TITLE}>Crew hours — leave blank if they did not work</h3>
          {crew.length === 0 ? (
            <p className="text-sm text-fieldiq-muted">No one is on this job&apos;s crew.</p>
          ) : (
            <div className="space-y-2">
              {crew.map((c, i) => (
                <div key={c.userId} className={ROW_CARD}>
                  <p className="text-sm font-extrabold text-white">{c.name}</p>
                  <div className="grid grid-cols-2 gap-2">
                    <EwField label="Hours">
                      <input className={EW_INPUT} inputMode="decimal" aria-label={`${c.name} hours`} value={c.hours} onChange={(e) => setCrewRow(i, { hours: e.target.value })} />
                    </EwField>
                    <EwField label="Standby">
                      <input className={EW_INPUT} inputMode="decimal" aria-label={`${c.name} standby hours`} value={c.standby} onChange={(e) => setCrewRow(i, { standby: e.target.value })} />
                    </EwField>
                  </div>
                  <EwField label="Notes">
                    <input className={EW_INPUT} aria-label={`${c.name} notes`} value={c.notes} onChange={(e) => setCrewRow(i, { notes: e.target.value })} />
                  </EwField>
                </div>
              ))}
            </div>
          )}
        </section>

        <section data-testid="ew-log-units">
          <h3 className={SECTION_TITLE}>Unit hours — leave blank if it did not run</h3>
          {units.length === 0 ? (
            <p className="text-sm text-fieldiq-muted">No owned unit is on this job and no rent-in is on rent on {ewDay(workDate)}.</p>
          ) : (
            <div className="space-y-2">
              {units.map((u, i) => {
                const options = checksFor(u);
                const filing = fileCheckState && u.equipmentId ? fileCheckState(u) : null;
                return (
                  <div key={u.key} data-testid={`ew-log-unit-${u.kind}`} className={ROW_CARD}>
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <p className="text-sm font-extrabold text-white">{u.name}</p>
                      <p className="text-xs text-fieldiq-muted">
                        {u.kind === 'rent_in' ? 'Rent-in' : 'Owned'}
                        {u.detail ? ` · ${u.detail}` : ''}
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <EwField label="Hours">
                        <input className={EW_INPUT} inputMode="decimal" aria-label={`${u.name} hours`} value={u.hours} onChange={(e) => setUnitRow(i, { hours: e.target.value })} />
                      </EwField>
                      <EwField label="Standby">
                        <input className={EW_INPUT} inputMode="decimal" aria-label={`${u.name} standby hours`} value={u.standby} onChange={(e) => setUnitRow(i, { standby: e.target.value })} />
                      </EwField>
                      {u.kind === 'owned' && (
                        <EwField label="Hour meter at end">
                          <input className={EW_INPUT} inputMode="decimal" aria-label={`${u.name} hour meter end`} value={u.meter} onChange={(e) => setUnitRow(i, { meter: e.target.value })} />
                        </EwField>
                      )}
                      <EwField label="Pre-use check">
                        <select
                          className={EW_INPUT}
                          aria-label={`${u.name} pre-use check`}
                          value={u.checkId}
                          disabled={!u.equipmentId}
                          onChange={(e) => setUnitRow(i, { checkId: e.target.value })}
                        >
                          <option value="">{!u.equipmentId ? 'Not a registered unit' : options.length ? 'None linked' : 'None filed this day'}</option>
                          {options.map((c) => (
                            <option key={c.id} value={c.id}>
                              {(c.status ?? '—').replace(/_/g, ' ')} · {c.checkedBy ?? '—'}
                            </option>
                          ))}
                          {u.checkId && !options.some((c) => c.id === u.checkId) && <option value={u.checkId}>Linked check</option>}
                        </select>
                      </EwField>
                    </div>
                    <EwField label="Notes">
                      <input className={EW_INPUT} aria-label={`${u.name} unit notes`} value={u.notes} onChange={(e) => setUnitRow(i, { notes: e.target.value })} />
                    </EwField>
                    {filing && !readOnly && (
                      <Link to={filing.to} state={filing.state} className="inline-flex min-h-[44px] items-center gap-1.5 text-sm font-bold text-fi-amber hover:underline">
                        <ClipboardCheck className="size-4" aria-hidden="true" />
                        File a pre-use check
                      </Link>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <section data-testid="ew-log-loads">
          <h3 className={SECTION_TITLE}>Loads hauled off and imported</h3>
          <div className="space-y-2">
            {loads.map((l, i) => (
              <div key={l.rid} data-testid="ew-log-load" className={ROW_CARD}>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-extrabold text-white">Load {i + 1}</p>
                  {!readOnly && <RowRemove label={`Remove load ${i + 1}`} onClick={() => setLoads((rows) => rows.filter((r) => r.rid !== l.rid))} />}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <EwField label="Direction">
                    <select className={EW_INPUT} aria-label={`Load ${i + 1} direction`} value={l.direction} onChange={(e) => setLoad(l.rid, { direction: e.target.value })}>
                      <Options list={catalog?.loadDirections} />
                    </select>
                  </EwField>
                  <EwField label="Material">
                    <select className={EW_INPUT} aria-label={`Load ${i + 1} material`} value={l.material} onChange={(e) => setLoad(l.rid, { material: e.target.value })}>
                      <Options list={catalog?.loadMaterials} blank="Choose…" />
                    </select>
                  </EwField>
                  <EwField label="Loads">
                    <input className={EW_INPUT} inputMode="numeric" aria-label={`Load ${i + 1} count`} value={l.count} onChange={(e) => setLoad(l.rid, { count: e.target.value })} />
                  </EwField>
                  <EwField label={quantityLabel(l)} hint="Optional">
                    <input className={EW_INPUT} inputMode="decimal" aria-label={`Load ${i + 1} quantity`} value={l.quantity} onChange={(e) => setLoad(l.rid, { quantity: e.target.value })} />
                  </EwField>
                  {!l.quantityUnit && (
                    <EwField label="Quantity unit">
                      <select className={EW_INPUT} aria-label={`Load ${i + 1} quantity unit`} value={l.quantityUnit} onChange={(e) => setLoad(l.rid, { quantityUnit: e.target.value })}>
                        <Options list={catalog?.quantityUnits} blank="Choose…" />
                      </select>
                    </EwField>
                  )}
                </div>
                <EwField label="Hauler">
                  <input className={EW_INPUT} aria-label={`Load ${i + 1} hauler`} value={l.hauler} onChange={(e) => setLoad(l.rid, { hauler: e.target.value })} />
                </EwField>
              </div>
            ))}
            {!readOnly && (
              <button
                type="button"
                className={`${EW_SECONDARY} w-full`}
                onClick={() =>
                  setLoads((rows) => [
                    ...rows,
                    { rid: rid(), direction: catalog?.loadDirections?.[0]?.value ?? 'haul_off', material: '', count: '1', quantity: '', quantityUnit: volume ?? '', hauler: '', notes: '' },
                  ])
                }
              >
                <Plus className="size-4" aria-hidden="true" />
                Add a load
              </button>
            )}
          </div>
        </section>

        {materialsKnown && (
          <section data-testid="ew-log-materials">
            <h3 className={SECTION_TITLE}>Materials used from stock</h3>
            <div className="space-y-2">
              {materials.map((m, i) => {
                const item = itemOf(m.materialId);
                const fromLog = logItemOf(m.materialId);
                const unitLabel = item?.unit_label ?? fromLog?.unit_label ?? null;
                return (
                  <div key={m.rid} data-testid="ew-log-material" className={ROW_CARD}>
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-extrabold text-white">Material {i + 1}</p>
                      {!readOnly && <RowRemove label={`Remove material ${i + 1}`} onClick={() => setMaterials((rows) => rows.filter((r) => r.rid !== m.rid))} />}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <EwField label="Item">
                        <select className={EW_INPUT} aria-label={`Material ${i + 1} item`} value={m.materialId} onChange={(e) => setMaterial(m.rid, { materialId: e.target.value, locationId: '', loggedBlank: false })}>
                          <option value="">Choose…</option>
                          {/* An item deleted since stays on the log under its name. */}
                          {fromLog && !item && <option value={fromLog.material_id}>{fromLog.name}</option>}
                          {stockItems.map((s) => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                          ))}
                        </select>
                      </EwField>
                      <EwField label={`Quantity used${unitLabel ? ` (${unitLabel})` : ''}`}>
                        <input className={EW_INPUT} inputMode="decimal" aria-label={`Material ${i + 1} quantity`} value={m.quantity} onChange={(e) => setMaterial(m.rid, { quantity: e.target.value })} />
                      </EwField>
                      <EwField label="Taken from" hint={m.loggedBlank ? 'Where the log took it from' : item?.location_name ? `Blank = ${item.location_name}` : 'Optional'}>
                        <select className={EW_INPUT} aria-label={`Material ${i + 1} location`} value={m.locationId} onChange={(e) => setMaterial(m.rid, { locationId: e.target.value, loggedBlank: false })}>
                          {/* A row read back with no location keeps what the log used; it is not "home". */}
                          <option value="">{m.loggedBlank ? 'As logged' : item?.location_name ? `Home (${item.location_name})` : 'No location'}</option>
                          {fromLog?.location_id && !stockLocations.some((l) => l.id === fromLog.location_id) && (
                            <option value={fromLog.location_id}>{fromLog.location_name ?? 'Former location'}</option>
                          )}
                          {stockLocations.map((l) => (
                            <option key={l.id} value={l.id}>{l.name}</option>
                          ))}
                        </select>
                      </EwField>
                      <div className="self-end pb-2 text-xs text-fieldiq-muted" data-testid="ew-log-material-on-hand">
                        {item ? `On hand: ${item.on_hand}${unitLabel ? ` ${unitLabel}` : ''}${item.negative ? ' — below zero' : item.low_stock ? ' — low' : ''}` : m.materialId ? 'On hand: —' : ''}
                      </div>
                    </div>
                  </div>
                );
              })}
              {!readOnly && (
                <button
                  type="button"
                  className={`${EW_SECONDARY} w-full`}
                  onClick={() => setMaterials((rows) => [...rows, { rid: rid(), materialId: '', locationId: '', quantity: '', loggedBlank: false }])}
                >
                  <Plus className="size-4" aria-hidden="true" />
                  Add a material
                </button>
              )}
              {picker.isSuccess && stockItems.length === 0 && materials.length === 0 && (
                <p className="text-xs text-fieldiq-muted">No inventory items yet. The office adds them under Inventory.</p>
              )}
            </div>
          </section>
        )}

        <section data-testid="ew-log-dumps">
          <h3 className={SECTION_TITLE}>Dump tickets</h3>
          <div className="space-y-2">
            {dumps.map((d, i) => (
              <div key={d.rid} data-testid="ew-log-dump" className={ROW_CARD}>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-extrabold text-white">Dump ticket {i + 1}</p>
                  {!readOnly && <RowRemove label={`Remove dump ticket ${i + 1}`} onClick={() => setDumps((rows) => rows.filter((r) => r.rid !== d.rid))} />}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <EwField label="Ticket #">
                    <input className={EW_INPUT} maxLength={50} aria-label={`Dump ticket ${i + 1} number`} value={d.ticketNumber} onChange={(e) => setDump(d.rid, { ticketNumber: e.target.value })} />
                  </EwField>
                  <EwField label="Facility">
                    <input className={EW_INPUT} maxLength={200} aria-label={`Dump ticket ${i + 1} facility`} value={d.facility} onChange={(e) => setDump(d.rid, { facility: e.target.value })} />
                  </EwField>
                  <EwField label="Material">
                    <select className={EW_INPUT} aria-label={`Dump ticket ${i + 1} material`} value={d.material} onChange={(e) => setDump(d.rid, { material: e.target.value })}>
                      <Options list={catalog?.loadMaterials} blank="—" />
                    </select>
                  </EwField>
                  <EwField label={`Tipping fee${currency ? ` (${currency})` : ''}`}>
                    <input className={EW_INPUT} inputMode="decimal" aria-label={`Dump ticket ${i + 1} tipping fee`} value={d.fee} onChange={(e) => setDump(d.rid, { fee: e.target.value })} />
                  </EwField>
                  <EwField label="Net weight">
                    <input className={EW_INPUT} inputMode="decimal" aria-label={`Dump ticket ${i + 1} weight`} value={d.weight} onChange={(e) => setDump(d.rid, { weight: e.target.value })} />
                  </EwField>
                  <EwField label="Weight unit">
                    <select className={EW_INPUT} aria-label={`Dump ticket ${i + 1} weight unit`} value={d.weightUnit} onChange={(e) => setDump(d.rid, { weightUnit: e.target.value })}>
                      <Options list={catalog?.weightUnits} blank="—" />
                    </select>
                  </EwField>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  {d.uploadId ? (
                    <>
                      {d.photoUrl ? (
                        <img src={d.photoUrl} alt={`Dump ticket ${d.ticketNumber || i + 1}`} className="h-16 w-16 rounded-fi-control object-cover" />
                      ) : (
                        <span className="text-xs text-fieldiq-text" data-testid="ew-log-dump-photo">
                          Photo attached{d.photoName ? ` · ${d.photoName}` : ''}
                        </span>
                      )}
                      {!readOnly && <RowRemove label={`Remove dump ticket ${i + 1} photo`} onClick={() => setDump(d.rid, { uploadId: null, photoName: null, photoUrl: null })} />}
                    </>
                  ) : (
                    !readOnly && (
                      <FilePick
                        label="Photo of the ticket"
                        testId={`ew-log-dump-file-${i}`}
                        disabled={uploading > 0}
                        onPick={(f) => upload(f, (uploadId) => setDump(d.rid, { uploadId, photoName: f.name, photoUrl: null }))}
                      />
                    )
                  )}
                </div>
              </div>
            ))}
            {!readOnly && (
              <button
                type="button"
                className={`${EW_SECONDARY} w-full`}
                onClick={() =>
                  setDumps((rows) => [
                    ...rows,
                    { rid: rid(), ticketNumber: '', facility: '', material: '', weight: '', weightUnit: mass ?? '', fee: '', uploadId: null, photoName: null, photoUrl: null, notes: '' },
                  ])
                }
              >
                <Plus className="size-4" aria-hidden="true" />
                Add a dump ticket
              </button>
            )}
          </div>
        </section>

        <section data-testid="ew-log-photos">
          <h3 className={SECTION_TITLE}>Before and after photos</h3>
          <div className="space-y-2">
            {photos.map((p, i) => (
              <div key={p.rid} data-testid="ew-log-photo" className={`${ROW_CARD} flex items-start gap-3 space-y-0`}>
                {p.url ? (
                  <img src={p.url} alt={`${catalogLabel(catalog?.photoStages, p.stage)} stage`} className="h-16 w-16 shrink-0 rounded-fi-control object-cover" />
                ) : (
                  <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-fi-control border border-fieldiq-border text-fieldiq-muted">
                    <Camera className="size-5" aria-hidden="true" />
                  </span>
                )}
                <div className="min-w-0 flex-1 space-y-1">
                  <p className="text-sm font-extrabold text-white">
                    {catalogLabel(catalog?.photoStages, p.stage)}
                    {p.name ? <span className="font-normal text-fieldiq-muted"> · {p.name}</span> : null}
                  </p>
                  <input className={EW_INPUT} aria-label={`Photo ${i + 1} caption`} placeholder="Caption" value={p.caption} onChange={(e) => setPhotos((rows) => rows.map((r) => (r.rid === p.rid ? { ...r, caption: e.target.value } : r)))} />
                  {!readOnly && <RowRemove label={`Remove photo ${i + 1}`} onClick={() => setPhotos((rows) => rows.filter((r) => r.rid !== p.rid))} />}
                </div>
              </div>
            ))}
            {!readOnly && (
              <div className="grid grid-cols-2 gap-2">
                <EwField label="Stage">
                  <select className={EW_INPUT} aria-label="Photo stage" value={photoStage} onChange={(e) => setPhotoStage(e.target.value)}>
                    <Options list={catalog?.photoStages} />
                  </select>
                </EwField>
                <div className="flex items-end">
                  <FilePick
                    label="Take a photo"
                    testId="ew-log-photo-file"
                    disabled={uploading > 0}
                    onPick={(f) => {
                      const stage = photoStage;
                      upload(f, (uploadId) => setPhotos((rows) => [...rows, { rid: rid(), stage, uploadId, name: f.name, url: null, caption: '' }]));
                    }}
                  />
                </div>
              </div>
            )}
            {uploading > 0 && <p className="text-sm text-fieldiq-text">Uploading…</p>}
          </div>
        </section>
      </fieldset>

      {!readOnly && (
        // Never disabled by a warning: only while the save itself is in flight.
        <button type="submit" className={`${EW_PRIMARY} w-full`} disabled={pending} data-testid="ew-log-save">
          {pending ? 'Saving…' : submitLabel}
        </button>
      )}
    </form>
  );
}
